/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/scheduleProposalApi.ts
 * PURPOSE: Employee tự đăng ký ca làm việc (web) — client cho 3 endpoint
 *          /api/v2/schedules/proposals/*. Trước 2026-09-04 luồng này CHỈ có ở
 *          mobile; web không có đường nào để nhân viên đăng ký ca.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: WFM — nhân viên tự đăng ký ca, quản lý duyệt
 * - Impact: Nhân viên chủ động xếp ca; quản lý duyệt trên lưới SC-009 [2d]
 * - Stakeholders: Nhân viên ca, Quản lý chi nhánh
 *
 * @technical-architecture
 * - Layer: API client (axios apiV2)
 * - Pattern: hợp đồng type khớp 1:1 schema backend
 * - Dependencies: services/api/config (apiV2 — tự gắn X-Tenant-Key)
 *
 * @cross-references
 * - Backend: backend/app/api/v2/endpoints/schedule_proposal.py
 * - Bản mobile (đối chiếu 1:1): mobile/src/services/api/scheduleProposalApi.ts
 * - Duyệt (quản lý, web): frontend/src/services/api/managerApi.ts
 *   (approveScheduleProposal / rejectScheduleProposal / reviewWeekProposals)
 */

import { apiV2 } from "./config";

export const SCHEDULE_PROPOSAL_ENDPOINTS = {
  SHIFT_CODES: "/schedules/proposals/shift-codes",
  WEEK: "/schedules/proposals/week",
} as const;

/** 1 mã ca trong danh mục — khớp ProposalShiftCodeItem (schedule_proposal.py). */
export interface ProposalShiftCodeItem {
  id: string;
  code: string | null;
  name: string;
  start_time: string;
  end_time: string;
  shift_type: string | null;
  hours: number;
  /** A4: mã ca ngày nghỉ (0 giờ) — đăng ký được mọi ngày (ECO-DEC-2026-144). */
  is_day_off?: boolean;
}

export interface ProposalShiftCodesResponse {
  items: ProposalShiftCodeItem[];
  total: number;
}

export type ProposalApprovalStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface ProposalShiftItem {
  schedule_id: string;
  date: string;
  day_label: string;
  shift_code: string | null;
  shift_label: string;
  start_time: string;
  end_time: string;
  is_day_off?: boolean;
  approval_status: ProposalApprovalStatus;
  rejection_reason: string | null;
  reviewed_at: string | null;
}

export interface WeekProposalResponse {
  week_start: string;
  week_end: string;
  items: ProposalShiftItem[];
}

export interface ProposalDayInput {
  date: string; // YYYY-MM-DD
  shift_template_id: string | null; // null = không đăng ký ngày này
}

export interface SubmitWeekProposalRequest {
  week_start: string;
  days: ProposalDayInput[];
}

export interface SubmitWeekProposalResponse {
  week_start: string;
  week_end: string;
  submitted_count: number;
  schedule_ids: string[];
  audited: boolean;
}

export const scheduleProposalApi = {
  /** GET /api/v2/schedules/proposals/shift-codes — danh mục mã ca quản lý đang dùng */
  getShiftCodes: async (): Promise<ProposalShiftCodesResponse> => {
    const res = await apiV2.get<ProposalShiftCodesResponse>(SCHEDULE_PROPOSAL_ENDPOINTS.SHIFT_CODES);
    return res.data;
  },

  /** GET /api/v2/schedules/proposals/week — đề xuất của CHÍNH mình trong tuần */
  getMyWeekProposal: async (weekStart: string): Promise<WeekProposalResponse> => {
    const res = await apiV2.get<WeekProposalResponse>(SCHEDULE_PROPOSAL_ENDPOINTS.WEEK, {
      params: { week_start: weekStart },
    });
    return res.data;
  },

  /**
   * POST /api/v2/schedules/proposals/week — gửi/sửa đề xuất tuần.
   * ⚠️ Backend THAY THẾ TOÀN BỘ đề xuất PENDING/REJECTED của tuần bằng danh
   * sách gửi lên (không cộng dồn) — nên payload phải chứa MỌI ngày chưa được
   * duyệt, kể cả ngày để trống (shift_template_id: null).
   */
  submitWeekProposal: async (
    payload: SubmitWeekProposalRequest,
  ): Promise<SubmitWeekProposalResponse> => {
    const res = await apiV2.post<SubmitWeekProposalResponse>(
      SCHEDULE_PROPOSAL_ENDPOINTS.WEEK,
      payload,
    );
    return res.data;
  },
};

export default scheduleProposalApi;
